import {
  buildAdminReimpersonateUrl,
  invalidateBffSession,
  isBffMode,
  probeBffSessionFresh,
  readBffUserId,
  redirectToBffLogin,
} from './bff';
import { activeClientSession, inClientZone, readClientPreview } from './client-session';

// focus/visibilitychange стреляют пачками при alt-tab — чаще, чем меняется владелец куки.
const RECHECK_COOLDOWN_MS = 5_000;
// Латч «ре-бут запрошен» живёт ровно столько, сколько нужно, чтобы состоявшийся reload() убрал документ:
// отменённый диалог beforeunload («Остаться») оставляет вкладку жить, и вечный латч убил бы сверку навсегда.
// Цена окна: 3 с после reload() сверка отдаёт 'changed' без probe — вкладка под подтверждённо чужой
// identity, 'student' — та же fail-closed политика; после окна сверка идёт в сеть заново.
const RELOAD_SETTLE_MS = 3_000;

let installed = false;
let lastUserId: string | null = null;
let lastCheckAt = 0;
let reloading = false;
let reloadLatchTimer: ReturnType<typeof setTimeout> | null = null;
// Пост-await откат кулдауна принадлежит последнему из стартовавших ре-чеков: более ранний, вернувшийся
// после него, не должен затирать чужую метку (пачка «нефорсированный + force» одного тика).
let recheckGeneration = 0;
let settledGeneration = 0;
let checkAtBeforeBurst = 0;
// Последняя сверка услышала от сервера «сессии нет», а не промолчала (сеть, не-200).
let lastProbeDead = false;

// jsdom запрещает подменять location.reload — шов существует только ради спеков.
let reload: () => void = () => window.location.reload();
let goToLogin: (returnUrl: string) => void = returnUrl => redirectToBffLogin(returnUrl);

/** Модульное состояние переживает тест: без сброса чужой lastUserId перетекает в следующий. */
export function resetIdentityWatchForTests(): void {
  installed = false;
  lastUserId = null;
  lastCheckAt = 0;
  reloading = false;
  if (reloadLatchTimer) clearTimeout(reloadLatchTimer);
  reloadLatchTimer = null;
  recheckGeneration = 0;
  settledGeneration = 0;
  checkAtBeforeBurst = 0;
  lastProbeDead = false;
  reload = () => window.location.reload();
  goToLogin = returnUrl => redirectToBffLogin(returnUrl);
}

export function setIdentityWatchReloadForTests(fn: () => void): void {
  reload = fn;
}

export function setIdentityWatchLoginForTests(fn: (returnUrl: string) => void): void {
  goToLogin = fn;
}

/**
 * bffUserId снимается один раз на буте вкладки: вход другого пользователя в соседней вкладке того же
 * профиля молча меняет владельца куки, а эта вкладка продолжает жить от имени прошлого — все её
 * запросы уходят под чужой identity и ловят честные 403. Ре-чек на возврате фокуса (и на 403 из
 * error.interceptor) сверяет identity с сервером; подтверждённая смена = ре-бут вкладки: гарды
 * разводят нового пользователя по его зоне, а стейт прошлой identity подмену не переживает.
 */
export function installIdentityWatch(): void {
  if (installed) return;
  installed = true;
  lastUserId = readBffUserId();
  window.addEventListener('focus', () => void recheckIdentity());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void recheckIdentity();
  });
}

export type IdentityVerdict = 'same' | 'changed' | 'unknown';

/**
 * Сверка identity с сервером с явным вердиктом — для резолвера редактора, которому «probe не ответил»
 * и «владелец тот же» нельзя выдавать за одно: первое обязано закрываться ученическим редактором
 * (fail-closed), второе пропускает. Подтверждённая смена владельца = ре-бут вкладки, как и по фокусу.
 * Кулдаун ре-чека сюда не относится. Сверка с сервером происходит на каждом входе в редактор, КРОМЕ
 * двух ранних выходов: не-BFF-режим (подменять нечего — куки bff_sid нет) и ре-бут, запрошенный в
 * последние RELOAD_SETTLE_MS (вердикт 'changed' переиспользуется, второй probe не нужен).
 * Оба выхода fail-closed или беспредметны.
 */
export async function verifyIdentity(): Promise<IdentityVerdict> {
  if (!isBffMode()) return 'same';
  if (reloading) return 'changed';

  // Кэш positive-вердикта пережил бы смену владельца куки — сверяемся только со свежим ответом, и
  // только своим: probe, ушедший раньше под прежней кукой, ответил бы про прежнего владельца.
  invalidateBffSession();
  const verdict = await probeBffSessionFresh();
  lastProbeDead = verdict === 'dead';
  // Пост-await: параллельная сверка уже запросила ре-бут — второй reload() тому же документу не нужен.
  if (reloading) return 'changed';
  // Смерть сессии здесь не вердикт: резолверу её решает 401-поток (probe + redirect в error.interceptor),
  // ре-чеку на фокусе — recheckIdentity.
  if (verdict !== 'alive') return 'unknown';

  const current = readBffUserId();
  if (lastUserId && current && current !== lastUserId) {
    requestReload();
    return 'changed';
  }

  lastUserId = current ?? lastUserId;
  return 'same';
}

function requestReload(): void {
  latchLeaving();
  reload();
}

function latchLeaving(): void {
  reloading = true;
  if (reloadLatchTimer) clearTimeout(reloadLatchTimer);
  reloadLatchTimer = setTimeout(() => {
    reloading = false;
    reloadLatchTimer = null;
  }, RELOAD_SETTLE_MS);
}

export async function recheckIdentity(options?: { force?: boolean }): Promise<void> {
  if (!installed || reloading || !isBffMode()) return;
  const now = Date.now();
  if (!options?.force && now - lastCheckAt < RECHECK_COOLDOWN_MS) return;
  // Метка ставится ДО await: пачка focus+visibilitychange одного тика схлопывается в один probe.
  const generation = ++recheckGeneration;
  // Первый в пачке запоминает метку ДО пачки: откат должен вернуть её, а не метку соседа по пачке.
  if (settledGeneration === generation - 1) checkAtBeforeBurst = lastCheckAt;
  lastCheckAt = now;

  const verdict = await verifyIdentity();
  settledGeneration = Math.max(settledGeneration, generation);
  // Неудачная проба (сеть, не-200) кулдаун не заслужила: иначе следующий ре-чек молчит 5 с
  // после того, как сервер так и не ответил, кто владеет кукой. Откатывает только последний из
  // стартовавших: более ранний не вправе затереть метку соседа, который мог ответить живым.
  // Обратная половина: если 'unknown' пришёл последним в пачке, откатывается и живая метка соседа —
  // лишний ре-чек раньше кулдауна, ошибка в сторону ac2 (потолок — один лишний запрос на пачку).
  if (verdict === 'unknown' && generation === recheckGeneration) lastCheckAt = checkAtBeforeBurst;
  if (verdict === 'unknown' && lastProbeDead && !options?.force && lastUserId && isPlainPhotographerTab()) leaveForLogin();
}

// Сессию фотографа снял выход или смена аккаунта в соседнем приложении этого браузера: без ухода
// на вход вкладка, вернувшаяся в фокус, показывала бы прежнего пользователя до первого запроса.
function leaveForLogin(): void {
  if (reloading) return;
  latchLeaving();
  goToLogin(window.location.pathname + window.location.search);
}

// Ученик, просмотр «как клиент» и «войти от имени» уходят каждый своей дорогой — её знает 401-поток
// error.interceptor, и на первом же запросе он её выберет. Здесь — только обычная вкладка фотографа.
function isPlainPhotographerTab(): boolean {
  return !inClientZone() && !activeClientSession() && !readClientPreview() && !buildAdminReimpersonateUrl();
}
